import { EntityState, createEntityAdapter } from '@ngrx/entity';
import { createReducer, on } from '@ngrx/store';
import {
  ChangeCheckedStatus,
  ChangeSelectOnlyNewStatus,
  LocaleGridSuccess,
  NewLiteralsData,
} from 'src/app/modules/administration/locale-management/actions/import-export-literals.actions';
import {
  LiteralsData,
  SearchLiteralsGridSuccess,
} from 'src/app/modules/administration/locale-management/actions/search-literals.actions';
import {
  StoreGridSuccess,
  TableCountryCodeChanged,
} from 'src/app/modules/data-customization/store-management/actions/search.actions';
import { SearchMerchant } from 'src/app/modules/merchant/actions/summary.actions';
import { v4 as uuidv4 } from 'uuid';
import { ActiveComponentChanged } from '../actions/eliqua.actions';
import {
  ClearGrid,
  DeselectedRowData,
  GridFailed,
  GridSearch,
  GridSuccess,
  LoadDisplayedColumns,
  SelectedRowDataChanged,
} from '../actions/grid.actions';
import { ItemsPerPageChanged, OrderingChanged, PagingChanged, SortingChanged } from '../actions/paginator.actions';
import { QcResponseGrid } from '../models/grid.model';
import { RecipeGridSuccess } from 'src/app/modules/automation-center/recipes/actions/search.actions';

export interface GridState extends EntityState<any> {
  showErrorMessage: boolean;
  messages: QcResponseGrid['messages'];
  displayedColumns: { [label: string]: string };
  selectedId: string;
  selectedRow: any;
  showSpinner: boolean;
  newLiteralsData: any[];
  literalsData: any[];
}
export const gridAdapter = createEntityAdapter<any>();

const initialState: GridState = gridAdapter.getInitialState({
  showErrorMessage: false,
  messages: [{ type: '', description: '', code: 0 }],
  displayedColumns: {},
  selectedId: '',
  selectedRow: [],
  showSpinner: false,
  newLiteralsData: [],
  literalsData: [],
});

export const GridReducer = createReducer(
  initialState,
  on(GridSearch, ItemsPerPageChanged, OrderingChanged, SortingChanged, PagingChanged, (state) => ({
    ...state,
    showSpinner: true,
  })),
  on(SearchMerchant, (state) => ({
    ...state,
    showSpinner: true,
  })),
  on(GridSuccess, (state, { res }) => {
    let data: any;
    let arrayData: any;
    if ('payload' in res) {
      data = res.payload.data.map((m: any) => ({ ...m, id: uuidv4() }));
    } else {
      arrayData = res;
      data = arrayData.map((m: any) => ({ ...m, id: uuidv4() }));
    }

    return {
      ...gridAdapter.setAll(data, state),
      messages: res.messages,
      showErrorMessage: true,
      selectedId: '',
      selectedRow: [],
      showSpinner: false,
    };
  }),
  on(StoreGridSuccess, (state, { res }) => {
    const data = res.payload.data.map((m: any) => ({
      ...m,
      storeId: m.id,
      id: uuidv4(),
      selectedLanguageCode: m.storeNames?.[0]?.languageCode || '',
    }));

    return {
      ...gridAdapter.setAll(data, state),
      messages: res.messages,
      showErrorMessage: true,
      selectedId: '',
      selectedRow: [],
      showSpinner: false,
    };
  }),
  on(RecipeGridSuccess, (state, { result }) => {
    const data = result.payload.data.map((m: any) => ({
      ...m,
      id: uuidv4()
    }));
    return {
      ...gridAdapter.setAll(data, state),
      messages: result.messages,
      showErrorMessage: true,
      selectedId: '',
      selectedRow: [],
      showSpinner: false,
    };
  }),
  on(GridFailed, () => initialState),
  on(ClearGrid, () => initialState),
  on(LoadDisplayedColumns, (state, { columns }) => ({
    ...state,
    displayedColumns: columns,
  })),
  on(SelectedRowDataChanged, (state, { row }) => ({
    ...state,
    selectedId: row.id,
    selectedRow: row,
  })),
  on(DeselectedRowData, (state) => ({
    ...state,
    selectedId: '',
    selectedRow: [],
  })),
  on(LocaleGridSuccess, (state, { res }) => {
    let data: any;
    let arrayData: any;

    if ('payload' in res) {
      data = res.payload.data.map((m: any) => ({ ...m, id: uuidv4() }));
    } else {
      arrayData = res;
      data = arrayData.map((m: any) => ({ ...m, id: uuidv4() }));
    }

    const updatedData = data.map((literal: any) => ({
      ...literal,
      isSelectedLiteral: true,
    }));

    return {
      ...gridAdapter.setAll(updatedData, state),
      messages: res.messages,
      showErrorMessage: true,
      selectedId: '',
      selectedRow: [],
      showSpinner: false,
    };
  }),
  on(SearchLiteralsGridSuccess, (state, { res }) => {
    let data: any;
    let arrayData: any;

    if ('payload' in res) {
      data = res.payload.data.map((m: any) => ({ ...m, id: uuidv4() }));
    } else {
      arrayData = res;
      data = arrayData.map((m: any) => ({ ...m, id: uuidv4() }));
    }

    const updatedData = data.map((literal: any) => ({
      ...literal,
    }));

    return {
      ...gridAdapter.setAll(updatedData, state),
      messages: res.messages,
      showErrorMessage: true,
      selectedId: '',
      selectedRow: [],
      showSpinner: false,
    };
  }),
  on(NewLiteralsData, (state, { newLiteralsData }) => ({
    ...state,
    newLiteralsData,
  })),
  on(LiteralsData, (state, { literalsData }) => ({
    ...state,
    literalsData,
  })),
  on(ChangeSelectOnlyNewStatus, (state, { selectOnlyNewStatus }) => {
    let status = selectOnlyNewStatus;

    if (status) {
      const updatedData = Object.values(state.entities).map((literal) => ({
        id: literal.id,
        changes: {
          ...literal,
          isSelectedLiteral: literal.newLiteral,
        },
      }));

      return {
        ...state,
        ...gridAdapter.updateMany(updatedData, state),
      };
    } else {
      const updatedData = Object.values(state.entities).map((literal) => ({
        id: literal.id,
        changes: {
          ...literal,
          isSelectedLiteral: false,
        },
      }));

      return {
        ...state,
        ...gridAdapter.updateMany(updatedData, state),
      };
    }
  }),
  on(ChangeCheckedStatus, (state, { isSelected, id }) => {
    const updatedLiteral = {
      id,
      changes: {
        isSelectedLiteral: isSelected,
      },
    };

    return {
      ...state,
      ...gridAdapter.updateOne(updatedLiteral, state),
    };
  }),
  on(TableCountryCodeChanged, (state, { id, languageCode }) => {
    const updatedLangCode = {
      id,
      changes: {
        selectedLanguageCode: languageCode,
      },
    };

    return {
      ...state,
      ...gridAdapter.updateOne(updatedLangCode, state),
    };
  }),
  on(ActiveComponentChanged, () => initialState),
);
